import { Http } from '../Utils/Http';
import { AxiosResponse } from 'axios';
import { IBcfAnalysisResponse, IBcfDefaults, IBcfImportPreview, IBcfImportResult, IBcfMappingConfig, IBcfImportSettings } from '../Store/BcfImport';

// Re-export interfaces for use in components
export type {
  IBcfAnalysisResponse,
  IBcfDefaults,
  IBcfImportPreview,
  IBcfImportResult,
  IBcfMappingConfig,
  IBcfImportSettings,
};

// API response wrapper from .NET
export interface ApiResponse<T> {
  success: boolean;
  data: T;
  info: string | null;
}

// Also need to export the field mapping interface
export interface IBcfFieldMapping {
  fieldType: 'status' | 'type' | 'priority' | 'assignee' | 'stage' | 'labels';
  bcfValues: string[];
  mappingRules: Record<string, string | number>;
}

/**
 * Um campo opcional conta como ignorado quando a pessoa marcou "ignorar" OU
 * quando não há o que mapear — o BCF não trouxe valor nenhum, ou nenhuma regra
 * foi montada. Sem isso o DTO saía dizendo `IgnoreUser: false` com
 * `UserMappings: {}`, ou seja, "considere o mapeamento" apontando para um
 * dicionário vazio.
 */
type IBcfMappingField = IBcfMappingConfig[keyof IBcfMappingConfig];

const isFieldIgnored = (field?: IBcfMappingField): boolean => {
  if (!field || field.isIgnored) { return true; }
  if (!field.bcfValues || field.bcfValues.length === 0) { return true; }
  return Object.keys(field.mappingRules || {}).length === 0;
};

const mappingRulesFor = (field?: IBcfMappingField): Record<string, string | number> => (
  isFieldIgnored(field) ? {} : (field?.mappingRules || {})
);

export class BcfImportApi {
  /**
   * Analyze uploaded BCF file
   * POST /api/bcf/analyze
   */
  static async analyzeBcfFile(file: File): Promise<AxiosResponse<ApiResponse<IBcfAnalysisResponse>>> {
    const formData = new FormData();
    formData.append('file', file);
    const axios = await Http.axios();
    return axios.post('/bcf/analyze', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  /**
   * Fetch default values for mappings
   * GET /api/bcf/defaults
   */
  static async fetchDefaults(constructionSiteId: string | number): Promise<AxiosResponse<ApiResponse<IBcfDefaults>>> {
    const axios = await Http.axios();
    return axios.get(`/bcf/defaults?constructionSiteId=${constructionSiteId}`);
  }

  /**
   * Validate mapping configuration
   * POST /api/bcf/validate-mapping
   */
  static async validateMapping(mappingConfig: IBcfMappingConfig): Promise<AxiosResponse<ApiResponse<{ isValid: boolean; errors?: string[] }>>> {
    const axios = await Http.axios();
    return axios.post('/bcf/validate-mapping', mappingConfig);
  }

  /**
   * Generate import preview
   * POST /api/bcf/preview
   */
  static async generatePreview(payload: {
    file: File;
    mappingConfigs: IBcfMappingConfig;
    settings: IBcfImportSettings;
  }): Promise<AxiosResponse<ApiResponse<IBcfImportPreview>>> {
    const formData = new FormData();
    formData.append('file', payload.file);
    formData.append('mappingConfigs', JSON.stringify(payload.mappingConfigs));
    formData.append('settings', JSON.stringify(payload.settings));

    const axios = await Http.axios();
    return axios.post('/bcf/preview', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  /**
   * Execute import
   * POST /api/bcf/import
   * Expects JSON body matching BcfImportMappingDto
   */
  static async executeImport(payload: {
    fileId: string;
    mappingConfigs: IBcfMappingConfig;
    importSettings?: IBcfImportSettings;
    constructionSiteId: string | number;
  }): Promise<AxiosResponse<ApiResponse<IBcfImportResult>>> {
    const settings = payload.importSettings;

    // Build the mapping DTO expected by the backend
    // For ignored fields, send empty object and set ignore flags so backend knows to skip them
    const mappingDto = {
      FileId: payload.fileId,
      ConstructionSiteId: Number(payload.constructionSiteId),
      StatusMappings: payload.mappingConfigs.status?.mappingRules || {},
      TypeMappings: payload.mappingConfigs.type?.mappingRules || {},
      PriorityMappings: mappingRulesFor(payload.mappingConfigs.priority),
      UserMappings: mappingRulesFor(payload.mappingConfigs.user),
      StageMappings: mappingRulesFor(payload.mappingConfigs.stage),
      LabelMappings: mappingRulesFor(payload.mappingConfigs.labels),
      DefaultDisciplineId: settings?.defaultDisciplineId || null,
      DefaultPointName: settings?.defaultPointName || null,
      DefaultAssignee: settings?.defaultAssignee || null,
      DefaultStage: settings?.defaultStage || null,
      UpdateExistingIssues: settings?.updateExistingIssues ?? false,
      CreateNewActivities: settings?.createNewActivities ?? true,
      AttachFiles: settings?.attachFiles ?? true,
      SkipUnmappedItems: settings?.skipUnmappedItems ?? false,
      IgnorePriority: isFieldIgnored(payload.mappingConfigs.priority),
      IgnoreUser: isFieldIgnored(payload.mappingConfigs.user),
      IgnoreStage: isFieldIgnored(payload.mappingConfigs.stage),
      IgnoreLabels: isFieldIgnored(payload.mappingConfigs.labels),
    };

    const axios = await Http.axios();
    return axios.post('/bcf/import', mappingDto);
  }

  /**
   * Export activities to BCF format
   * POST /api/bcf/export
   * Returns 202 Accepted - export runs in background
   */
  static async exportBcf(payload: {
    constructionSiteId: string | number;
    version?: string;
    topicGuids?: string[];
  }): Promise<AxiosResponse<ApiResponse<{ queued: boolean }>>> {
    const axios = await Http.axios();
    const exportDto = {
      ConstructionSiteId: Number(payload.constructionSiteId),
      Version: payload.version || '3.0',
      TopicGuids: payload.topicGuids || null,
    };
    return axios.post('/bcf/export', exportDto);
  }

  /**
   * Download exported BCF file
   * GET /api/bcf/DownloadExport
   * Returns file blob for download
   */
  static async downloadExport(fileId: string): Promise<AxiosResponse<Blob>> {
    const axios = await Http.axios();
    return axios.get('/bcf/DownloadExport', {
      params: { fileId },
      responseType: 'blob',
    });
  }
}
